import Markdown from "react-markdown";

import { ContentCopyRounded, ThumbDownOffAltRounded, ThumbUpOutlined } from "@mui/icons-material";
import { Box, IconButton } from "@mui/material";

import { MarketingSummaryAnalyticsService } from "@analytics/index";

import ScaleIPLogo from "@components/ScaleIPLogo";

import theme from "@theme/index";

import { handleCopyToClipboard } from "@utilities/copyToClipboard";

interface IAssistantChatBubbleProperties {
  handleCopyClick?: () => void;
  handleNegativeFeedbackClick?: () => void;
  handlePositiveFeedbackClick?: () => void;
  handleRegenerateClick?: () => void;
  isRated?: boolean;
  isShowControls?: boolean;
  isStreaming?: boolean;
  message: string;
}

const AssistantChatBubble = ({
  handleNegativeFeedbackClick,
  handlePositiveFeedbackClick,
  isRated,
  isShowControls = false,
  isStreaming = false,
  message,
}: IAssistantChatBubbleProperties) => {
  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      <Box>
        <ScaleIPLogo />
      </Box>
      <Box>
        <Box
          sx={{
            "& h4": {
              margin: 0,
              marginTop: 2,
            },
            "& p": {
              margin: 0,
            },
            "& ul": {
              marginTop: 1,
            },
            backgroundColor: theme.palette.grey[100],
            borderRadius: "8px",
            px: 2,
            py: 2,
          }}
        >
          <Markdown>{message}</Markdown>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
          {isStreaming ? (
            <Box
              sx={{
                alignItems: "center",
                display: "inline-flex",
                gap: "2px",
                height: "1em",
                marginTop: "4px",
              }}
            >
              {[0, 1, 2].map((index) => (
                <Box
                  key={index}
                  sx={{
                    "@keyframes pulsingDot": {
                      "0%, 100%": {
                        opacity: 0.4,
                        transform: "scale(0.8)",
                      },
                      "50%": {
                        opacity: 1,
                        transform: "scale(1)",
                      },
                    },
                    animation: "pulsingDot 1.4s infinite",
                    animationDelay: `${index * 0.2}s`,
                    backgroundColor: "currentColor",
                    borderRadius: "50%",
                    height: "4px",
                    width: "4px",
                  }}
                />
              ))}
            </Box>
          ) : (
            <span></span>
          )}
          {isShowControls && (
            <Box
              sx={{
                alignItems: "center",
                color: theme.palette.grey[600],
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <IconButton
                onClick={() => {
                  handleCopyToClipboard(message);
                  MarketingSummaryAnalyticsService.trackCopyResponseClicked();
                }}
                sx={{ height: "35px", width: "35px" }}
              >
                <ContentCopyRounded
                  sx={{
                    "&:hover": { color: theme.palette.info.main },
                    cursor: "pointer",
                    transition: "color 0.1s ease-in-out",
                  }}
                />
              </IconButton>

              {!isRated && (
                <>
                  <IconButton onClick={handlePositiveFeedbackClick} sx={{ height: "35px", width: "35px" }}>
                    <ThumbUpOutlined
                      sx={{
                        "&:hover": { color: theme.palette.primary.main },
                        cursor: "pointer",
                        transition: "color 0.1s ease-in-out",
                      }}
                    />
                  </IconButton>
                  <IconButton onClick={handleNegativeFeedbackClick} sx={{ height: "35px", width: "35px" }}>
                    <ThumbDownOffAltRounded
                      sx={{
                        "&:hover": { color: theme.palette.error.main },
                        cursor: "pointer",
                        transition: "color 0.1s ease-in-out",
                      }}
                    />
                  </IconButton>
                </>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AssistantChatBubble;
